import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faCheck,
  faCheckDouble,
  faTrash,
  faFilter,
  faSpinner,
  faArrowLeft,
  faBellSlash
} from '@fortawesome/free-solid-svg-icons';
import { useNotifications } from '../context/NotificationContext';
import Navbar from './Navbar';
import '../assets/css/notifications-page.css';

const Notifications = () => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    fetchNotifications
  } = useNotifications();

  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'order_update', 'new_message', etc.
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    await fetchNotifications();
    setLoading(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order_update':
        return 'fa-box';
      case 'new_message':
        return 'fa-comment';
      case 'price_alert':
        return 'fa-tag';
      case 'payment_update':
        return 'fa-credit-card';
      case 'wallet_update':
        return 'fa-wallet';
      case 'product_update':
        return 'fa-shopping-bag';
      case 'dispute_update':
        return 'fa-gavel';
      default:
        return 'fa-bell';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'order_update':
        return '#3b82f6'; // blue
      case 'new_message':
        return '#8b5cf6'; // purple
      case 'price_alert':
        return '#f59e0b'; // amber
      case 'payment_update':
        return '#10b981'; // green
      case 'wallet_update':
        return '#22c55e'; // primary green
      case 'product_update':
        return '#06b6d4'; // cyan
      case 'dispute_update':
        return '#ef4444'; // red
      default:
        return '#6b7280'; // gray
    }
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - notifTime) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;

    return notifTime.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleMarkAsRead = async (notificationId, e) => {
    e.stopPropagation();
    setActionLoading(notificationId);
    await markAsRead(notificationId);
    setActionLoading(null);
  };

  const handleMarkAllAsRead = async () => {
    setActionLoading('all');
    await markAllAsRead();
    setActionLoading(null);
  };

  const handleDelete = async (notificationId, e) => {
    e.stopPropagation();
    setActionLoading(notificationId);
    await deleteNotification(notificationId);
    setActionLoading(null);
  };

  const getFilteredNotifications = () => {
    let filtered = [...notifications];

    // Filter by read/unread status
    if (filter === 'unread') {
      filtered = filtered.filter(n => !n.read && !n.is_read);
    } else if (filter === 'read') {
      filtered = filtered.filter(n => n.read || n.is_read);
    }

    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(n => n.type === typeFilter);
    }

    return filtered;
  };

  const filteredNotifications = getFilteredNotifications();

  const notificationTypes = [
    { value: 'all', label: 'All' },
    { value: 'order_update', label: 'Orders' },
    { value: 'new_message', label: 'Messages' },
    { value: 'payment_update', label: 'Payments' },
    { value: 'wallet_update', label: 'Wallet' },
    { value: 'product_update', label: 'Products' },
    { value: 'price_alert', label: 'Price Alerts' },
    { value: 'dispute_update', label: 'Disputes' }
  ];

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="notifications-page-container">
          <div className="notifications-loading">
            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            <p>Loading notifications...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="notifications-page-container">
        {/* Header */}
        <div className="notifications-page-header">
          <button onClick={() => navigate(-1)} className="notifications-back-btn">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <div className="notifications-title-section">
            <div className="notifications-title">
              <FontAwesomeIcon icon={faBell} />
              <h1>Notifications</h1>
              {unreadCount > 0 && (
                <span className="notifications-unread-badge">{unreadCount} new</span>
              )}
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="notifications-mark-all-btn"
              disabled={actionLoading === 'all'}
            >
              {actionLoading === 'all' ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckDouble} />
                  Mark all as read
                </>
              )}
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="notifications-filters">
          <div className="notifications-filter-group">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All ({notifications.length})
            </button>
            <button
              className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
              onClick={() => setFilter('unread')}
            >
              Unread ({unreadCount})
            </button>
            <button
              className={`filter-btn ${filter === 'read' ? 'active' : ''}`}
              onClick={() => setFilter('read')}
            >
              Read ({notifications.length - unreadCount})
            </button>
          </div>

          <div className="notifications-type-filter">
            <FontAwesomeIcon icon={faFilter} />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="type-filter-select"
            >
              {notificationTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Notifications List */}
        <div className="notifications-page-list">
          {filteredNotifications.length === 0 ? (
            <div className="notifications-empty-state">
              <FontAwesomeIcon icon={faBellSlash} size="4x" />
              <h2>No notifications</h2>
              <p>
                {filter === 'unread'
                  ? "You're all caught up! No unread notifications."
                  : typeFilter !== 'all'
                  ? `No ${typeFilter.replace('_', ' ')} notifications.`
                  : 'You have no notifications yet.'}
              </p>
            </div>
          ) : (
            filteredNotifications.map((notification) => {
              const notifId = notification._id || notification.id;
              const isRead = notification.read || notification.is_read;
              const isLoading = actionLoading === notifId;

              return (
                <div
                  key={notifId}
                  className={`notification-page-item ${!isRead ? 'unread' : ''}`}
                >
                  <div
                    className="notification-icon-wrapper"
                    style={{ backgroundColor: getNotificationColor(notification.type) }}
                  >
                    <FontAwesomeIcon icon={['fas', getNotificationIcon(notification.type)]} />
                  </div>

                  <div className="notification-page-content">
                    <div className="notification-page-header">
                      <h3 className="notification-page-title">{notification.title}</h3>
                      <span className="notification-page-time">
                        {formatTime(notification.timestamp || notification.created_at)}
                      </span>
                    </div>
                    <p className="notification-page-message">{notification.message}</p>
                  </div>

                  <div className="notification-page-actions">
                    {!isRead && (
                      <button
                        onClick={(e) => handleMarkAsRead(notifId, e)}
                        className="notification-page-action-btn read"
                        title="Mark as read"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                          <FontAwesomeIcon icon={faCheck} />
                        )}
                      </button>
                    )}
                    <button
                      onClick={(e) => handleDelete(notifId, e)}
                      className="notification-page-action-btn delete"
                      title="Delete"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <FontAwesomeIcon icon={faTrash} />
                      )}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};

export default Notifications;
